import { ReactNode, useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { CrossIcon } from '@honestica/curecall-ui'
import { useBodyScrollLock } from './use-body-scroll-lock.hook'
import { useFocusTrap } from './use-focus-trap.hook'
import { twMerge } from 'src/utils/tw-merge.util'

interface SideDrawerProps {
  title: ReactNode
  closeLabel: string
  onClose: VoidFunction
  children: ReactNode
  /**
   * The second column shown next to the main one. The drawer is expanded while `isDetailOpen` is true.
   * `detail` is rendered as long as it is provided, so a consumer can keep it mounted for the duration
   * of the closing transition by passing it with `isDetailOpen` false.
   */
  detail?: ReactNode
  isDetailOpen?: boolean
  detailTitle?: ReactNode
  detailSubtitle?: ReactNode
  onDetailClose?: VoidFunction
}

const SideDrawer = ({
  title,
  closeLabel,
  onClose,
  children,
  detail,
  isDetailOpen = false,
  detailTitle,
  detailSubtitle,
  onDetailClose,
}: SideDrawerProps) => {
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)

  useBodyScrollLock()
  useFocusTrap(dialogRef)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      if (isDetailOpen && onDetailClose) {
        onDetailClose()
      } else {
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isDetailOpen, onDetailClose, onClose])

  return createPortal(
    <div
      data-testid="side-drawer-backdrop"
      className="c-fixed c-inset-0 c-z-[60] c-bg-black/25"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={twMerge(
          'c-absolute c-inset-y-0 c-right-0 c-max-w-full c-overflow-hidden c-border c-border-black/[0.08] c-bg-white c-outline-none c-transition-[width] c-duration-300',
          isDetailOpen ? 'c-w-[1200px]' : 'c-w-[398px]',
        )}
      >
        {detail !== undefined && (
          <section
            aria-label={typeof detailTitle === 'string' ? detailTitle : undefined}
            aria-hidden={!isDetailOpen}
            className={twMerge(
              'c-absolute c-inset-y-0 c-right-[398px] c-flex c-w-[calc(min(1200px,100vw)_-_398px)] c-py-6 c-pl-6',
              // Once closed, the column is hidden after the drawer has shrunk, which also removes it from the tab order.
              isDetailOpen ? 'c-visible' : 'c-invisible c-transition-[visibility] c-delay-300 c-duration-0',
            )}
          >
            <div className="c-flex c-min-w-0 c-flex-1 c-flex-col c-gap-6 c-overflow-hidden c-rounded-2xl c-bg-drawer-surface c-p-4">
              <header className="c-flex c-items-center c-justify-between c-gap-4">
                <div className="c-flex c-min-w-0 c-flex-col c-gap-1">
                  <h3 className="c-m-0 c-text-sm c-font-semibold c-leading-[19px] c-text-black">
                    {detailTitle}
                  </h3>
                  {detailSubtitle}
                </div>
                {onDetailClose && (
                  <button
                    type="button"
                    aria-label={closeLabel}
                    onClick={onDetailClose}
                    className="c-text-black hover:c-text-grey-600"
                  >
                    <CrossIcon className="c-size-6" />
                  </button>
                )}
              </header>
              <div className="c-min-h-0 c-flex-1 c-overflow-auto c-overscroll-contain">{detail}</div>
            </div>
          </section>
        )}

        <div className="c-absolute c-inset-y-0 c-right-0 c-flex c-w-[398px] c-flex-col c-gap-4 c-p-6">
          <div className="c-flex c-flex-col c-gap-2">
            <header className="c-flex c-items-center c-justify-between c-gap-4">
              <h2 id={titleId} className="c-m-0 c-text-xl c-font-semibold c-leading-[27px] c-text-black">
                {title}
              </h2>
              <button
                type="button"
                aria-label={closeLabel}
                onClick={onClose}
                className="c-text-black hover:c-text-grey-600"
              >
                <CrossIcon className="c-size-6" />
              </button>
            </header>
            <div className="c-border-t c-border-grey-200" />
          </div>
          <div className="c-flex c-min-h-0 c-flex-1 c-flex-col c-gap-4 c-overflow-y-auto c-overscroll-contain">
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export { SideDrawer }
